<script setup lang="ts">
import { EditableArea } from "@magnolia/vue-editor";
import { Heading } from "~/components/clarity-ui/Heading";
import { Radio } from "~/components/clarity-ui/Radio";
import { RadioGroup } from "~/components/clarity-ui/Radio/RadioGroup";
import useCommonLabels from "~/composables/useCommonLabels";
import useBritaMiddleware from "~/composables/useBritaMiddleware";
import EmptyArea from "~/templates/areas/EmptyArea.vue";
import FeedbackArea from "~/templates/areas/FeedbackArea.vue";
import InputField from "~/templates/elements/form/InputField.vue";
import TextAreaField from "~/templates/elements/form/TextAreaField.vue";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import type { mgnlHeadline } from "~/types/magnolia";
import { isEditMode as evaluateEditMode } from "~/utils/content/magnolia";
import { useRoute } from "#app";

export interface NewsletterUnsubscribeProps {
  bottom?: object;
  commonLabel?: string;
  main?: object;
  successArea?: {
    headline?: string;
    richText?: string;
  };
  unsubscribeArea?: {
    commonLabel?: {
      name?: string;
      value?: string;
    }[];
    copy?: string;
    headline?: mgnlHeadline;
    reasonText?: string;
  };
}

const { unsubscribeArea } = defineProps<NewsletterUnsubscribeProps>();

defineOptions({
  inheritAttrs: false,
});

const { getLabels } = useCommonLabels();
const { pushError } = useNotifications();
const { unsubscribe } = useBritaMiddleware();

const showFreetext = ref(false);
const isEditMode = ref(evaluateEditMode());
const isSuccess = ref(false);

const formLabels = getLabels("BWF/components/newsletter/unsubscribe/form/", [
  "emailLabel",
  "otherReasonLabel",
  "reasonMessagePlaceholder",
  "errorMessage",
]);

const { query } = useRoute();
// if mw is hashing the email, we need the commented line
const unhashedEmail = atob(query.token ?? query.encodedEmail ?? query.email ?? "");
// const unhashedEmail = query.email;

const formState = reactive({
  // query param
  email: unhashedEmail ?? "",
  // query param (myBITA y/n)
  source: query.source ?? "bwf",
  reasonMessage: "",
  reasonId: "",
  freetext: "",
});

const selectableReasons =
  unsubscribeArea?.commonLabel?.map(({ name, value }) => ({
    value: name,
    label: value,
  })) ?? [];

// add hardcoded "other reason" which toggles freetext
const OTHER_REASON_ID = "other";
selectableReasons.push({
  value: OTHER_REASON_ID,
  label: formLabels?.otherReasonLabel,
});

watch(formState, () => {
  // copy corresponding label from chosen reason
  formState.reasonMessage = selectableReasons.find(
    (reason) => reason.value === formState.reasonId,
  ).label;
});

const onSubmit = async () => {
  try {
    await unsubscribe(formState);
    isSuccess.value = true;
  } catch (e) {
    pushError(formLabels?.errorMessage);
  }
};
</script>

<template>
  <main class="container grid grid-cols-12">
    <EditableArea v-if="main" class="col-span-full" :content="main" />
    <EditableArea
      v-if="isSuccess || isEditMode"
      class="col-span-full"
      :content="successArea"
      :custom-view="FeedbackArea"
    />
    <EditableArea
      v-if="!isSuccess || isEditMode"
      :content="unsubscribeArea"
      :custom-view="EmptyArea"
      class="col-span-full my-12 grid gap-4 md:col-start-2 md:col-end-12 md:grid-cols-8 xl:col-start-3 xl:col-end-11 xl:gap-6"
    >
      <Heading class="text-center md:col-span-full" color="blue" size="xlarge" tag="h1">
        {{ unsubscribeArea?.headline.text }}
      </Heading>
      <div
        class="prose prose-black col-span-full mb-6 max-w-full text-center xl:mb-2"
        v-html="unsubscribeArea?.copy"
      />

      <form class="col-span-full md:col-start-2 md:col-end-8" @submit.prevent="onSubmit()">
        <div class="mb-6 xl:mb-8">
          <InputField
            v-model="formState.email"
            class="form-field-grey w-full"
            name="E-Mail"
            :label="formLabels?.emailLabel"
            readonly
          />
        </div>
        <div
          class="prose prose-black mb-6 max-w-full xl:mb-8"
          v-html="unsubscribeArea?.reasonText"
        />
        <RadioGroup v-model="formState.reasonId"  name="unsubscribe-reason">
          <Radio
            v-for="reason in selectableReasons"
            :key="reason.value"
            class="mb-4"
            :value="reason.value"
            @change="showFreetext = reason.value === OTHER_REASON_ID"
          >
            {{ reason.label }}
          </Radio>
        </RadioGroup>

        <TextAreaField
          v-if="showFreetext"
          v-model="formState.freetext"
          name="message"
          maxlength="250"
          :placeholder="formLabels?.reasonMessagePlaceholder"
        />

        <button class="btn btn-blue mt-4 w-full xl:mt-6">
          <CommonLabel path="BWF/components/newsletter/unsubscribe/form/sendButtonLabel" />
        </button>
      </form>
    </EditableArea>
    <EditableArea v-if="bottom" class="col-span-full" :content="bottom" />
  </main>
</template>
